export interface DebounceOptions {
  maxWait?: number;
}
export interface DebouncedFunction<TArgs extends unknown[]> {
  (...args: TArgs): void;
  cancel: () => void;
}

export const debounce = <TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  wait: number,
  options: DebounceOptions = {}
): DebouncedFunction<TArgs> => {
  const { maxWait } = options;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let maxWaitId: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: TArgs | null = null;
  let maxWaitStart: number | null = null;

  const invoke = () => {
    if (lastArgs !== null) {
      fn(...lastArgs);
      lastArgs = null;
    }
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (maxWaitId !== null) {
      clearTimeout(maxWaitId);
      maxWaitId = null;
    }
    maxWaitStart = null;
  };

  const cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (maxWaitId !== null) {
      clearTimeout(maxWaitId);
      maxWaitId = null;
    }
    lastArgs = null;
    maxWaitStart = null;
  };

  const debounced = (...args: TArgs) => {
    const now = Date.now();
    lastArgs = args;

    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(invoke, wait);

    if (maxWait !== undefined && maxWaitStart === null) {
      maxWaitStart = now;
      maxWaitId = setTimeout(invoke, maxWait);
    }
  };

  debounced.cancel = cancel;
  return debounced;
};
